import React, { useMemo, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import { toast } from "react-toastify";
import { Bar, Line } from "react-chartjs-2";
import { ChevronRight } from "lucide-react";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

const COLORS = ["#0f172a", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#14b8a6", "#f97316"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function LinkSessions() {
    const [level, setLevel] = useState("year");
    const [year, setYear] = useState(null);
    const [month, setMonth] = useState(null);
    const [day, setDay] = useState(null);
    const [availableYears, setAvailableYears] = useState([]);
    const [availableMonths, setAvailableMonths] = useState([]);
    const [availableDays, setAvailableDays] = useState([]);
    const [loading, setLoading] = useState(false);
    const [labels, setLabels] = useState([]);
    const [series, setSeries] = useState([]);
    const [collectLoading, setCollectLoading] = useState(false);

    const loadData = async (next = {}) => {
        const nextLevel = next.level ?? level;
        const nextYear = next.year ?? year;
        const nextMonth = next.month ?? month;
        const nextDay = next.day ?? day;
        setLoading(true);
        try {
            const params = { granularity: nextLevel };
            if (nextYear) params.year = nextYear;
            if (nextMonth) params.month = nextMonth;
            if (nextLevel === "day" && nextYear && nextMonth && nextDay) {
                params.date = `${nextYear}-${String(nextMonth).padStart(2, "0")}-${String(nextDay).padStart(2, "0")}`;
            }

            const { data } = await axiosInstance.get("/link-session/series", { params });
            setLabels(data?.labels || []);
            setSeries(data?.series || []);

            const meta = data?.meta || {};
            const years = Array.isArray(meta.available_years) ? meta.available_years : [];
            const months = Array.isArray(meta.available_months) ? meta.available_months : [];
            const days = Array.isArray(meta.available_days) ? meta.available_days : [];

            setAvailableYears(years);
            setAvailableMonths(months);
            setAvailableDays(days);

            if (!nextYear && years.length) setYear(years[0]);
        } catch (err) {
            toast.error(err?.response?.data?.error || "Failed to load link session data");
            setLabels([]);
            setSeries([]);
        } finally {
            setLoading(false);
        }
    };

    const collectData = async () => {
        setCollectLoading(true);
        try {
            const statDate = day && month && year ? `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}` : undefined;
            const { data } = await axiosInstance.post("/link-session/collect", {
                ...(statDate && { stat_date: statDate }),
            });
            toast.success(data?.message || "Link session data collected successfully");
            // Reload data to reflect changes
            loadData();
        } catch (err) {
            toast.error(err?.response?.data?.error || "Failed to collect link session data");
        } finally {
            setCollectLoading(false);
        }
    };

    React.useEffect(() => {
        loadData({ level: "year" });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const chartData = useMemo(
        () => ({
            labels,
            datasets: series.map((s, idx) => ({
                label: s.link_name,
                data: s.values || [],
                borderColor: COLORS[idx % COLORS.length],
                backgroundColor: COLORS[idx % COLORS.length],
                tension: 0.25,
                pointRadius: level === "day" ? 0 : 2,
                pointHoverRadius: 5,
                borderWidth: level === "day" ? 0 : 3,
            })),
        }),
        [labels, series, level]
    );
    // const chatOptions = useMemo (() => { console.log() })

    const chartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: "nearest", intersect: true },
            plugins: {
                legend: { position: "bottom" },
                tooltip: {
                    mode: "nearest",
                    intersect: true,
                    callbacks: {
                        label: (context) => {
                            const label = context.dataset.label || "";
                            const value = context.parsed?.y ?? context.parsed ?? "";
                            return label ? `${label}: ${value}` : `${value}`;
                        },
                    },
                },
                datalabels: { display: false },
                title: {
                    display: true,
                    text:
                        level === "year"
                            ? `Monthly Sessions (${year || "-"})`
                            : level === "month"
                            ? `Daily Sessions (${year || "-"}-${String(month || "").padStart(2, "0")})`
                            : `Sessions on ${year || "-"}-${String(month || "").padStart(2, "0")}-${String(day || "").padStart(2, "0")}`,
                },
            },
            scales: {
                y: {
                    beginAtZero: true,
                    ticks: { precision: 0 },
                    title: { display: true, text: "Sessions" },
                },
                x: {
                    title: { display: true, text: level === "year" ? "Month" : level === "month" ? "Day" : "Date" },
                },
            },
        }),
        [level, year, month, day]
    );

    const chipBase = "h-9 rounded-full border px-3 text-sm font-semibold transition";
    const chipIdle = "border-slate-300 bg-white text-slate-700 hover:bg-slate-50";
    const chipActive = "border-slate-900 bg-slate-900 text-white";

    return (
        <div className="min-h-screen bg-slate-50 px-3 sm:px-4 lg:px-6 py-6">
            <div className="mx-auto w-full max-w-7xl space-y-4">
                <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-extrabold text-slate-900">Link Session Trends</h1>
                            <p className="text-sm text-slate-600 mt-1">Year → Month → Day drill-down. One line per link.</p>
                        </div>
                        <button
                            onClick={collectData}
                            disabled={collectLoading}
                            className="h-fit whitespace-nowrap rounded-lg bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:bg-slate-400"
                        >
                            {collectLoading ? "Collecting..." : "Collect Data"}
                        </button>
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-slate-600">
                        <button
                            className="rounded-lg border border-slate-300 px-2.5 py-1.5 hover:bg-slate-50"
                            onClick={() => {
                                setLevel("year");
                                setMonth(null);
                                setDay(null);
                                loadData({ level: "year", year });
                            }}
                        >
                            Year
                        </button>
                        <ChevronRight className="h-4 w-4" />
                        <span>{year || "-"}</span>
                        {month ? (
                            <>
                                <ChevronRight className="h-4 w-4" />
                                <span>{MONTHS[month - 1]}</span>
                            </>
                        ) : null}
                        {day ? (
                            <>
                                <ChevronRight className="h-4 w-4" />
                                <span>{day}</span>
                            </>
                        ) : null}
                    </div>

                    <div className="mt-4">
                        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Years</div>
                        <div className="flex flex-wrap gap-2">
                            {availableYears.map((y) => (
                                <button
                                    key={y}
                                    className={`${chipBase} ${year === y ? chipActive : chipIdle}`}
                                    onClick={() => {
                                        setYear(y);
                                        setLevel("year");
                                        setMonth(null);
                                        setDay(null);
                                        loadData({ level: "year", year: y, month: null, day: null });
                                    }}
                                >
                                    {y}
                                </button>
                            ))}
                        </div>
                    </div>

                    {year ? (
                        <div className="mt-4">
                            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Months</div>
                            <div className="flex flex-wrap gap-2">
                                {availableMonths.map((m) => (
                                    <button
                                        key={m}
                                        className={`${chipBase} ${month === m && level !== "year" ? chipActive : chipIdle}`}
                                        onClick={() => {
                                            setMonth(m);
                                            setLevel("month");
                                            setDay(null);
                                            loadData({ level: "month", year, month: m, day: null });
                                        }}
                                    >
                                        {MONTHS[m - 1]}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : null}

                    {year && month ? (
                        <div className="mt-4">
                            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Days</div>
                            <div className="flex flex-wrap gap-2">
                                {availableDays.map((d) => (
                                    <button
                                        key={d}
                                        className={`${chipBase} ${day === d && level === "day" ? chipActive : chipIdle}`}
                                        onClick={() => {
                                            setDay(d);
                                            setLevel("day");
                                            loadData({ level: "day", year, month, day: d });
                                        }}
                                    >
                                        {d}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : null}
                </section>

                <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
                    <div className="h-[420px]">
                        {loading ? (
                            <div className="text-sm text-slate-500">Loading...</div>
                        ) : level === "day" ? (
                            <Bar data={chartData} options={chartOptions} />
                        ) : (
                            <Line data={chartData} options={chartOptions} />
                        )}
                    </div>
                    {!labels.length && !loading ? <div className="mt-4 text-sm text-slate-500">No data for this level.</div> : null}
                </section>
            </div>
        </div>
    );
}
